import { plainToInstance, Type } from 'class-transformer';
import { IsNumber, IsOptional, IsIn, IsBoolean, IsNotEmpty, Max, Min, IsString, MaxLength, IsDateString, IsDate } from 'class-validator';
import {
  INVOICE_PRINT_NOT_SHOW_DATE,
  INVOICE_PRINT_SHOW_MONTH,
  INVOICE_PRINT_SHOW_ALL_DATE,
  InvoiceStatus,
  WccDto,
  InvoiceRowDto,
  InvoiceReportRowDto,
  PayOrderDto,
} from '../main';
import { CompanyDto } from './company.dto';
import { ContractDto, ContractInfoDto } from './contract.dto';
import { INVOICE_TYPE_CLIENT, INVOICE_TYPE_SUPPLIER } from './enums/invoice-types';
import { LegalEntityDto } from './legal-entity.dto';
import { UserBaseDto, UserDto } from './user.dto';
import { ContractAppendixDto } from './contract-appendix.dto';

export type InvoiceTotals = {
  priceTotal: number;
  discountPriceTotal: number;
  partialPaidAmount: number;
  priceExclVatTotal: number;
  vatValueTotal: number;
  countAmount: number;
};

export class CreateAdvanceInvoiceDto {
  @IsString()
  @IsNotEmpty()
  documentNumber: string;

  @IsNumber()
  invoiceId?: number;

  @Type(() => Date)
  @IsDate()
  createdAt: Date;
}

export class AdvanceInvoiceDto extends CreateAdvanceInvoiceDto {
  id: number;
}

export class InvoiceBasicResource {
  id: number;

  @Type()
  startDate: Date;

  @IsOptional()
  @Type()
  endDate?: Date;

  @IsNumber()
  managerId: number;

  @IsBoolean()
  printFormShowMediaLink: boolean;

  @IsNumber()
  @IsIn([INVOICE_PRINT_NOT_SHOW_DATE, INVOICE_PRINT_SHOW_MONTH, INVOICE_PRINT_SHOW_ALL_DATE])
  printFormShowDateViewState: number;

  @IsOptional()
  @IsBoolean()
  generateWcc?: boolean;

  @IsOptional()
  @IsNumber()
  legalEntityTax?: number;

  @IsBoolean()
  invoiceSupplier?: boolean;

}

export class CreateInvoiceDto {
  @Type()
  startDate: Date;

  @IsOptional()
  @Type()
  endDate: Date;

  invoiceNumber?: string;

  @IsNumber()
  sellerCompanyId: number;

  @IsNumber()
  sellerLegalEntityId: number;

  @IsOptional()
  @IsNumber()
  sellerSignerId: number;

  @IsOptional()
  @IsNumber()
  sellerBankDetailsId: number;

  @IsNumber()
  buyerCompanyId: number;

  @IsNumber()
  buyerLegalEntityId: number;

  @IsOptional()
  @IsNumber()
  buyerBankDetailsId: number;

  @IsOptional()
  @IsNumber()
  contractId: number;

  @IsNumber()
  @IsIn([INVOICE_TYPE_CLIENT, INVOICE_TYPE_SUPPLIER])
  type: number;

  @IsNumber()
  managerId: number;

  @IsOptional()
  @IsBoolean()
  generateWcc: boolean;
}

// export type InvoiceInfo = {
//   id: number;
//   invoiceNumber: string;
//   status: InvoiceStatus;
//   manager: UserDto;
//
//   sellerCompany: CompanyDto;
//   seller: LegalEntityDto;
//
//   buyerCompany: CompanyDto;
//   buyer: LegalEntityDto;
//
//   startDate: string;
//   endDate: string;
//
//   totals: InvoiceTotals;
// }

export class InvoiceInfoDto {
  @IsNumber()
  id: number;

  invoiceNumber: string;
  
  @IsNumber()
  @IsIn([INVOICE_TYPE_CLIENT, INVOICE_TYPE_SUPPLIER])
  type: number;

  @Type(() => Number)
  status: InvoiceStatus;

  @Type(() => UserDto)
  manager: UserDto;

  @Type(() => CompanyDto)
  sellerCompany: CompanyDto;

  @Type(() => LegalEntityDto)
  seller: LegalEntityDto;

  @Type(() => CompanyDto)
  buyerCompany: CompanyDto;
  @Type(() => LegalEntityDto)
  buyer: LegalEntityDto;

  @Type(() => Date)
  startDate: Date;

  @IsOptional()
  @Type(() => Date)
  endDate?: Date;

  totals: InvoiceTotals;

  wcc: WccDto | null;

  contract: ContractInfoDto;

  @IsBoolean()
  isInternetAdvertising: boolean;

  @Type(() => Number)
  legalEntityTax: number;

  report: {
    rows: InvoiceReportRowDto[];
    clientPayments: PayOrderDto[];
  } | null;
}

export class InvoiceBasicInfoDto {
  @IsNumber()
  id?: number;

  invoiceNumber: string;

  @Type(() => Number)
  status: InvoiceStatus;
}

export class InvoiceDto extends CreateInvoiceDto {
  @IsNumber()
  id: number;

  invoiceNumber: string;

  @Type(() => Number)
  status: InvoiceStatus;

  @Type(() => UserDto)
  manager: UserDto;

  @Type(() => ContractInfoDto)
  contract: ContractInfoDto;

  @Type(() => CompanyDto)
  sellerCompany: CompanyDto;

  @Type(() => LegalEntityDto)
  seller: LegalEntityDto;

  @Type(() => CompanyDto)
  buyerCompany: CompanyDto;

  @Type(() => LegalEntityDto)
  buyer: LegalEntityDto;

  totals: InvoiceTotals;

  @IsBoolean()
  printFormShowMediaLink: boolean;

  @IsNumber()
  @IsIn([INVOICE_PRINT_NOT_SHOW_DATE, INVOICE_PRINT_SHOW_MONTH, INVOICE_PRINT_SHOW_ALL_DATE])
  printFormShowDateViewState: number;

  static fromPlain(obj: any) {
    return plainToInstance<InvoiceDto, any>(InvoiceDto, obj);
  }

  managerPercent: number;

  appendix: ContractAppendixDto;

  legalEntityTax: number;

  rows?: InvoiceRowDto[];

  @IsBoolean()
  isInternetAdvertising: boolean;
}

export class UpdateInvoiceResource extends InvoiceBasicResource {}

export class InvoiceCanceledStatusDto {
  @IsBoolean()
  isCanceled: boolean;
}

export class SetInvoiceLegalEntityTaxDto {
  @IsNumber()
  @Max(100)
  @Min(0)
  @IsNotEmpty()
  legalEntityTax: number;
}

export class CopyInvoiceDto {
  @IsNumber()
  @IsNotEmpty()
  invoiceId: number;

  @IsOptional()
  @IsString()
  @MaxLength(512)
  invoiceNumber?: string;

  /**
   * Переопределение реквизитов продавца для копии.
   * Используется, когда исходные реквизиты привязаны к заблокированному расчётному счёту
   * и пользователь выбрал другие в диалоге копирования.
   */
  @IsOptional()
  @IsNumber()
  sellerBankDetailsId?: number;
}

/** Код ошибки: реквизиты продавца привязаны к заблокированному для исходящих счетов расчётному счёту */
export const SELLER_BANK_ACCOUNT_BLOCKED_CODE = 'SELLER_BANK_ACCOUNT_BLOCKED';

export class LastClosedInvoiceDto {
  @Type(() => InvoiceDto)
  invoice: InvoiceDto;

  vat: number;
}

export class InvoiceForCashFlowReportDto {
  id: number;
  invoiceNumber: string;

  @Type(() => Number)
  status: InvoiceStatus;

  @Type(() => UserBaseDto)
  manager: UserBaseDto;

  @Type(() => Date)
  startDate: Date;

  @IsOptional()
  @Type(() => Date)
  endDate?: Date;

  legalEntityTax: number;

  report: {
    rows: InvoiceReportRowDto[];
    clientPayments: PayOrderDto[];
  } | null;
}
